"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { domAnimation, LazyMotion, m } from "framer-motion";
import {
	ArrowRight,
	Eye,
	EyeOff,
	Loader2,
	Play,
	TrendingUp,
	Users,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useUserAuth } from "@/contexts/user_auth_context";

const loginSchema = z.object({
	email: z.string().email("Invalid email address"),
	password: z.string().min(1, "Password is required"),
});

type LoginInput = z.infer<typeof loginSchema>;

export default function UserLoginPage() {
	const router = useRouter();
	const { login, isAuthenticated, isLoading: authLoading } = useUserAuth();

	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginInput>({
		resolver: zodResolver(loginSchema),
	});

	// Redirect if already authenticated
	useEffect(() => {
		if (!authLoading && isAuthenticated) {
			router.push("/dashboard");
		}
	}, [authLoading, isAuthenticated, router]);

	const onSubmit = async (data: LoginInput) => {
		setIsSubmitting(true);
		setError(null);

		try {
			const result = await login(data.email, data.password);

			if (result.success) {
				// Use the redirect path from the API response (role-based)
				router.push(result.redirectPath || "/dashboard");
			} else {
				setError(result.error || "Login failed");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	// Show loading while checking auth
	if (authLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-white">
				<div className="flex flex-col items-center gap-4">
					<div className="w-12 h-12 border-4 border-zinc-200 border-t-red-600 rounded-full animate-spin" />
				</div>
			</div>
		);
	}

	// Don't render if already authenticated
	if (isAuthenticated) {
		return null;
	}

	return (
		<LazyMotion features={domAnimation}>
			<div className="flex min-h-screen bg-white text-zinc-900">
				{/* Left side - Brand showcase */}
				<div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950">
					{/* Animated gradient background */}
					<div className="absolute inset-0">
						<div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-600/20 via-transparent to-zinc-950" />
						<div className="absolute top-1/4 -left-20 w-96 h-96 bg-red-600/30 rounded-full blur-[100px] animate-pulse" />
						<div
							className="absolute bottom-1/4 -right-20 w-80 h-80 bg-red-500/20 rounded-full blur-[80px] animate-pulse"
							style={{ animationDelay: "1s" }}
						/>
					</div>

					{/* Grid pattern overlay */}
					<div
						className="absolute inset-0 opacity-[0.03]"
						style={{
							backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
							backgroundSize: "60px 60px",
						}}
					/>

					{/* Content */}
					<div className="relative z-10 flex flex-col justify-between w-full p-12">
						{/* Logo */}
						<m.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
						>
							<h1 className="font-syne text-3xl font-bold text-white tracking-tight">
								TheHighraysYT
							</h1>
						</m.div>

						{/* Center content */}
						<m.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							className="space-y-8"
						>
							<div className="space-y-4">
								<h2 className="font-syne text-5xl font-bold text-white leading-tight">
									Track Your
									<br />
									<span className="text-red-500">Growth Journey</span>
								</h2>
								<p className="text-zinc-400 text-lg max-w-md leading-relaxed">
									Access your purchase history, monitor campaign progress, and
									manage your video ad campaigns.
								</p>
							</div>

							{/* Feature highlights */}
							<div className="flex flex-col gap-4">
								<div className="flex items-center gap-3 text-zinc-300">
									<div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
										<Play className="w-5 h-5 text-red-500" />
									</div>
									<span className="text-sm">Real-time campaign tracking</span>
								</div>
								<div className="flex items-center gap-3 text-zinc-300">
									<div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
										<TrendingUp className="w-5 h-5 text-red-500" />
									</div>
									<span className="text-sm">Detailed analytics & reports</span>
								</div>
								<div className="flex items-center gap-3 text-zinc-300">
									<div className="w-10 h-10 rounded-lg bg-red-600/20 flex items-center justify-center">
										<Users className="w-5 h-5 text-red-500" />
									</div>
									<span className="text-sm">Priority customer support</span>
								</div>
							</div>
						</m.div>

						{/* Bottom stats */}
						<m.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.8, delay: 0.6 }}
							className="flex gap-12"
						>
							<div>
								<p className="font-syne text-3xl font-bold text-white">500M+</p>
								<p className="text-xs text-zinc-500 uppercase tracking-wider">
									Views Delivered
								</p>
							</div>
							<div>
								<p className="font-syne text-3xl font-bold text-white">10K+</p>
								<p className="text-xs text-zinc-500 uppercase tracking-wider">
									Happy Clients
								</p>
							</div>
						</m.div>
					</div>
				</div>

				{/* Right side - Login form */}
				<div className="flex w-full lg:w-1/2 items-center justify-center p-8 lg:p-16 xl:p-24">
					<m.div
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="w-full max-w-md space-y-8"
					>
						{/* Mobile brand */}
						<div className="lg:hidden text-center mb-8">
							<h1 className="font-syne text-3xl font-bold tracking-tight text-zinc-900">
								TheHighraysYT
							</h1>
						</div>

						<div className="space-y-2">
							<h2 className="font-syne text-3xl font-bold text-zinc-900">
								Sign In
							</h2>
							<p className="text-zinc-500">
								Access your dashboard and track your campaigns
							</p>
						</div>

						{error && (
							<m.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								className="p-4 bg-red-50 border border-red-200 rounded-xl"
							>
								<p className="text-sm text-red-600">{error}</p>
							</m.div>
						)}

						<form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
							<div className="space-y-2">
								<label
									htmlFor="email"
									className="text-sm font-medium text-zinc-700 mb-1.5 block"
								>
									Email Address
								</label>
								<input
									id="email"
									type="email"
									placeholder="you@example.com"
									autoComplete="email"
									disabled={isSubmitting}
									className="w-full h-12 px-4 border border-zinc-200 rounded-xl bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-[box-shadow,border-color,opacity] disabled:opacity-50"
									{...register("email")}
								/>
								{errors.email && (
									<p className="text-xs text-red-600">{errors.email.message}</p>
								)}
							</div>

							<div className="space-y-2">
								<label
									htmlFor="password"
									className="text-sm font-medium text-zinc-700 mb-1.5 block"
								>
									Password
								</label>
								<div className="relative">
									<input
										id="password"
										type={showPassword ? "text" : "password"}
										placeholder="Enter your password"
										autoComplete="current-password"
										disabled={isSubmitting}
										className="w-full h-12 px-4 pr-12 border border-zinc-200 rounded-xl bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-[box-shadow,border-color,opacity] disabled:opacity-50"
										{...register("password")}
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										aria-label={
											showPassword ? "Hide password" : "Show password"
										}
										className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
									>
										{showPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
								{errors.password && (
									<p className="text-xs text-red-600">
										{errors.password.message}
									</p>
								)}
							</div>

							<button
								type="submit"
								disabled={isSubmitting}
								className="w-full h-12 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-[background-color,box-shadow,opacity] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-600/20 hover:shadow-red-600/30"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="h-4 w-4 animate-spin" />
										Signing in...
									</>
								) : (
									<>
										Sign In
										<ArrowRight className="h-4 w-4" />
									</>
								)}
							</button>
						</form>

						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-zinc-200" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-white px-4 text-zinc-400">
									New to TheHighraysYT?
								</span>
							</div>
						</div>

						<p className="text-center text-sm text-zinc-500">
							<Link
								href="/auth/signup"
								className="text-red-600 hover:text-red-700 font-semibold transition-colors"
							>
								Create an account
							</Link>{" "}
							to get started with your growth journey
						</p>
					</m.div>
				</div>
			</div>
		</LazyMotion>
	);
}
